import type { ReactNode } from 'react';
import { createContext, useContext, useEffect, useRef } from 'react';
import type { AppBundle } from '../utils/app-bundles';

interface AppBundleData {
  initialized: boolean;
  loaded: boolean;
  appWrapper: HTMLDivElement;
  cleanup: () => void;
}

interface BackgroundAppBundleContextType {
  getBackgroundAppData: (appId: AppBundle) => AppBundleData;
  setBackgroundAppData: (appId: AppBundle, data: Partial<AppBundleData>) => void;
}

const BackgroundAppBundleContext = createContext<BackgroundAppBundleContextType | undefined>(undefined);

export function isBackgroundAppBundlesEnabled() {
  // TODO: Using a manually set localStorage flag for now, because we need this value before we have
  // account data for an actual feature flag. Maybe we can use the feature flag to set this value?
  // This works for now, but we should probably find a better solution.
  try {
    return localStorage.getItem('hydra:background_app_bundles') === 'true';
  } catch {
    // If there's an error accessing localStorage, we'll assume it's not enabled.
    // Some privacy settings may prevent access to localStorage, though not
    // sure if this still happens, but better safe than sorry.
    return false;
  }
}

export function useBackgroundAppBundle(appId: AppBundle) {
  const context = useContext(BackgroundAppBundleContext);
  if (context === undefined) {
    throw new Error('useBackgroundAppBundle must be used within an BackgroundAppBundleContextProvider');
  }

  return {
    getData: () => context.getBackgroundAppData(appId),
    setData: (data: Partial<AppBundleData>) => context.setBackgroundAppData(appId, data),
  };
}

export function BackgroundAppBundleContextProvider({ children }: { children: ReactNode }) {
  const appDataMapRef = useRef<Record<AppBundle, AppBundleData>>({} as Record<AppBundle, AppBundleData>);

  const getBackgroundAppData = (appId: AppBundle) => {
    return appDataMapRef.current[appId];
  };

  const setBackgroundAppData = (appId: AppBundle, data: Partial<AppBundleData>) => {
    appDataMapRef.current = {
      ...appDataMapRef.current,
      [appId]: {
        ...appDataMapRef.current[appId],
        ...data,
      },
    };
  };

  useEffect(function cleanupOnUnmount() {
    return () => {
      for (const appData of Object.values(appDataMapRef.current)) {
        if (appData.appWrapper != null) {
          appData.appWrapper.remove();
        }
        if (appData.cleanup != null) {
          appData.cleanup();
        }
      }
    };
  }, []);

  return (
    <BackgroundAppBundleContext.Provider value={{ getBackgroundAppData, setBackgroundAppData }}>
      {children}
    </BackgroundAppBundleContext.Provider>
  );
}
